<template>
  <div class="customize-page light-panel">
    <section class="customize-section">
      <div class="container container--move-left">
        <div class="product-detail">
          <div class="product-detail__main">
            <div class="customize-gallery reveal">
              <div class="customize-gallery__hero">
                <button
                  type="button"
                  class="customize-gallery__frame"
                  @click="openLightbox(activeGalleryIndex)"
                >
                  <img :src="page.detailGallery[activeGalleryIndex]" alt="">
                  <span class="customize-gallery__corner" aria-hidden="true"></span>
                  <span class="customize-gallery__more">{{ $t('customize.morePictures') }}</span>
                </button>
              </div>
              <div class="customize-gallery__thumbs">
                <button
                  v-for="(image, index) in page.detailGallery.slice(0, 3)"
                  :key="image"
                  type="button"
                  class="customize-gallery__thumb"
                  :class="{ 'is-active': activeGalleryIndex === index }"
                  @click="activeGalleryIndex = index"
                >
                  <img :src="image" alt="">
                </button>
              </div>
            </div>
          </div>

          <aside class="product-detail__side reveal">
            <h1 class="product-detail__title">{{ page.detailTitle }}</h1>

            <div class="customize-surface-craft">
              <h3 class="customize-surface-craft__title">{{ $t('customize.surfaceCraft') }}</h3>

              <fieldset class="customize-fieldset customize-fieldset--chips">
                <div class="customize-chips">
                  <label
                    v-for="item in surfaceCraftOptions"
                    :key="item.chip"
                    class="customize-chip"
                  >
                    <input
                      v-model="selectedCraft"
                      type="radio"
                      name="craft"
                      :value="item.chip"
                    >
                    <span>{{ item.title }}</span>
                  </label>
                </div>
              </fieldset>
            </div>

            <form class="customize-form" @submit.prevent="submitRequest">
              <fieldset class="customize-fieldset">
                <legend>{{ $t('customize.typeOfPlank') }}</legend>
                <div class="customize-chips customize-chips--type">
                  <label
                    v-for="item in plankTypeOptions"
                    :key="item.slug || item.label"
                    class="customize-chip customize-chip--type"
                  >
                    <input v-model="selectedType" type="radio" name="type" :value="item.label">
                    <span>{{ item.label }}</span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="customize-fieldset">
                <legend>{{ $t('customize.length') }}</legend>
                <div class="customize-chips">
                  <label v-for="item in page.lengths" :key="item" class="customize-chip">
                    <input v-model="selectedLength" type="radio" name="length" :value="item">
                    <span>{{ item }}</span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="customize-fieldset">
                <legend>{{ $t('customize.width') }}</legend>
                <div class="customize-chips">
                  <label v-for="item in page.widths" :key="item" class="customize-chip">
                    <input v-model="selectedWidth" type="radio" name="width" :value="item">
                    <span>{{ item }}</span>
                  </label>
                </div>
              </fieldset>

              <fieldset class="customize-fieldset">
                <legend>{{ $t('customize.thickness') }}</legend>
                <div class="customize-chips">
                  <label v-for="item in page.thicknesses" :key="item" class="customize-chip">
                    <input v-model="selectedThickness" type="radio" name="thickness" :value="item">
                    <span>{{ item }}</span>
                  </label>
                </div>
              </fieldset>

              <button type="submit" class="customize-request" :disabled="requestSubmitting">{{ requestSubmitting ? $t('common.submitting') : $t('common.request') }}</button>
              <p v-if="requestMessage" class="customize-feedback">{{ requestMessage }}</p>
            </form>
          </aside>
        </div>
      </div>
    </section>

    <section class="customize-references">
      <div class="container">
        <div class="customize-references__head reveal">
          <div class="customize-references__titles">
            <p>{{ page.referencePreTitle }}</p>
            <h2>{{ page.referenceTitle }}</h2>
          </div>
          <div class="customize-references__controls">
            <button type="button" class="customize-slider-btn" :disabled="!canScrollPrev" @click="scrollReferences(-1)">
              <span aria-hidden="true">‹</span>
              <span class="visually-hidden">{{ $t('common.previousSlide') }}</span>
            </button>
            <button type="button" class="customize-slider-btn" :disabled="!canScrollNext" @click="scrollReferences(1)">
              <span aria-hidden="true">›</span>
              <span class="visually-hidden">{{ $t('common.nextSlide') }}</span>
            </button>
          </div>
        </div>
      </div>
      <div ref="referenceTrack" class="customize-references__track" @scroll="updateReferenceScroll">
        <article
          v-for="item in page.references"
          :key="item.title + item.image"
          class="customize-reference-card"
        >
          <div class="customize-reference-card__media">
            <img :src="item.image" :alt="item.title" loading="lazy">
          </div>
          <h3>{{ item.title }}</h3>
        </article>
      </div>
    </section>

    <section class="customize-advice">
      <div class="customize-advice__image">
        <img :src="page.adviceImage" alt="">
      </div>
      <div class="customize-advice__content">
        <h2>{{ page.adviceTitle }}</h2>
        <form class="customize-advice__form" @submit.prevent="submitAdvice">
          <label>
            <span>{{ $t('common.email') }} <em>*</em></span>
            <input v-model.trim="adviceForm.email" type="email" required>
          </label>
          <label>
            <span>{{ $t('common.zipCode') }} <em>*</em></span>
            <input v-model.trim="adviceForm.zip" type="text" required>
          </label>
          <label>
            <span>{{ $t('common.country') }} <em>*</em></span>
            <select v-model="adviceForm.country" required>
              <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
              <option v-for="country in countries" :key="country" :value="country">{{ country }}</option>
            </select>
          </label>
          <label>
            <span>{{ $t('common.messageToUs') }}</span>
            <textarea v-model.trim="adviceForm.message" rows="5"></textarea>
          </label>
          <button type="submit" :disabled="adviceSubmitting">{{ adviceSubmitting ? $t('common.submitting') : $t('common.submit') }}</button>
          <p v-if="adviceMessage" class="customize-feedback">{{ adviceMessage }}</p>
        </form>
      </div>
    </section>

    <transition name="fade">
      <div v-if="lightboxOpen" class="customize-lightbox" @click.self="closeLightbox">
        <button type="button" class="customize-lightbox__close" :aria-label="$t('common.close')" @click="closeLightbox">×</button>
        <button type="button" class="customize-lightbox__nav customize-lightbox__nav--prev" @click="stepLightbox(-1)">‹</button>
        <img :src="page.detailGallery[lightboxIndex]" alt="">
        <button type="button" class="customize-lightbox__nav customize-lightbox__nav--next" @click="stepLightbox(1)">›</button>
      </div>
    </transition>
  </div>
</template>

<script>
import site from '../mock/strongova'
import {
  fetchPublicCases,
  fetchPublicCategories,
  fetchPublicPageSections,
  normalizeCase,
  resolveAssetUrl,
  submitCustomRequest
} from '../api/public'

const BESPOKE_CHIP = '__bespoke__'

function clonePage () {
  return JSON.parse(JSON.stringify(site.customizePage))
}

export default {
  name: 'CustomizePage',
  data () {
    return {
      page: clonePage(),
      countries: site.contact.countries,
      productPageSections: [],
      productCategories: [],
      activeGalleryIndex: 0,
      selectedCraft: '',
      selectedType: '',
      selectedLength: '',
      selectedWidth: '90',
      selectedThickness: '',
      requestSubmitting: false,
      requestMessage: '',
      adviceSubmitting: false,
      adviceMessage: '',
      lightboxOpen: false,
      lightboxIndex: 0,
      canScrollPrev: false,
      canScrollNext: true,
      adviceForm: {
        email: '',
        zip: '',
        country: '',
        message: ''
      }
    }
  },
  computed: {
    productSurfaceSection () {
      return this.productPageSections.find(item => item.pageKey === 'products' && item.sectionKey === 'surface') || null
    },
    surfaceCraftOptions () {
      const fallbackImage = site.hero.background
      let items = []

      if (this.productSurfaceSection && Array.isArray(this.productSurfaceSection.imagesJson) && this.productSurfaceSection.imagesJson.length) {
        items = this.productSurfaceSection.imagesJson.map(item => ({
          chip: item.title,
          title: item.title,
          image: resolveAssetUrl(item.image, fallbackImage)
        }))
      } else if (Array.isArray(site.productsPage.surfaces) && site.productsPage.surfaces.length) {
        items = site.productsPage.surfaces.map(item => ({
          chip: item.title,
          title: item.title,
          image: resolveAssetUrl(item.image, fallbackImage)
        }))
      } else {
        items = (this.page.craftOptions || []).map(item => ({
          chip: item.chip || item.title,
          title: item.title,
          image: resolveAssetUrl(item.image, fallbackImage)
        }))
      }

      return [
        ...items,
        {
          chip: BESPOKE_CHIP,
          title: this.$t('customize.bespoke'),
          image: ''
        }
      ]
    },
    plankTypeOptions () {
      if (this.productCategories.length) {
        return this.productCategories.map(item => ({
          label: item.name,
          slug: item.slug
        }))
      }

      return (this.page.plankTypes || []).map(item => ({
        label: item.label,
        slug: item.slug || ''
      }))
    },
    selectedCraftLabel () {
      const match = this.surfaceCraftOptions.find(item => item.chip === this.selectedCraft)
      return match ? match.title : this.selectedCraft
    }
  },
  created () {
    this.loadProductOptions()
    fetchPublicPageSections({ pageKey: 'customize' })
      .then(sections => {
        if (Array.isArray(sections) && sections.length) {
          this.applyPageSections(sections)
        }
      })
      .catch(() => {
        this.page = clonePage()
      })
      .then(() => {
        this.syncDefaultSelections()
      })
    this.loadCases()
  },
  mounted () {
    this.$nextTick(this.updateReferenceScroll)
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    window.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
  },
  watch: {
    surfaceCraftOptions () {
      this.syncDefaultSelections()
    },
    plankTypeOptions () {
      this.syncDefaultSelections()
    },
  },
  methods: {
    applyPageSections (sections) {
      const page = clonePage()
      const hero = sections.find(item => item.sectionKey === 'hero')
      const gallery = sections.find(item => item.sectionKey === 'gallery')
      const references = sections.find(item => item.sectionKey === 'content')
      const contact = sections.find(item => item.sectionKey === 'contact')

      if (hero) {
        page.detailTitle = hero.title || page.detailTitle
      }

      if (gallery && Array.isArray(gallery.imagesJson) && gallery.imagesJson.length) {
        page.detailGallery = gallery.imagesJson
          .map(item => resolveAssetUrl(item.image))
          .filter(Boolean)
      }

      if (references) {
        page.referenceTitle = references.title || page.referenceTitle
        page.referencePreTitle = references.description || page.referencePreTitle
      }

      if (contact) {
        page.adviceTitle = contact.title || page.adviceTitle
        page.adviceImage = resolveAssetUrl(contact.image, page.adviceImage)
      }

      this.page = page
      this.activeGalleryIndex = 0
      this.lightboxIndex = 0
      this.$nextTick(this.updateReferenceScroll)
    },
    loadCases () {
      fetchPublicCases()
        .then(cases => {
          if (Array.isArray(cases) && cases.length) {
            const fallbackImage = this.page.references[0] && this.page.references[0].image
            this.page.references = cases
              .map(item => {
                const normalized = normalizeCase(item, fallbackImage)
                return {
                  title: normalized.title,
                  image: normalized.image
                }
              })
              .filter(item => item.image)
            this.$nextTick(this.updateReferenceScroll)
          }
        })
        .catch(() => {})
    },
    loadProductOptions () {
      Promise.all([
        fetchPublicPageSections({ pageKey: 'products' }),
        fetchPublicCategories({ type: 'product' })
      ])
        .then(([sections, categories]) => {
          this.productPageSections = Array.isArray(sections) ? sections : []
          this.productCategories = Array.isArray(categories) ? categories : []
          this.syncDefaultSelections()
        })
        .catch(() => {
          this.productPageSections = []
          this.productCategories = []
          this.syncDefaultSelections()
        })
    },
    syncDefaultSelections () {
      const crafts = this.surfaceCraftOptions
      if (crafts.length && !crafts.some(item => item.chip === this.selectedCraft)) {
        this.selectedCraft = crafts[0].chip
      }

      const planks = this.plankTypeOptions
      if (planks.length && !planks.some(item => item.label === this.selectedType)) {
        this.selectedType = planks[0].label
      }
    },
    openLightbox (index) {
      this.lightboxIndex = index
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },
    closeLightbox () {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    },
    stepLightbox (delta) {
      const total = this.page.detailGallery.length
      this.lightboxIndex = (this.lightboxIndex + delta + total) % total
    },
    onKeydown (event) {
      if (!this.lightboxOpen) return
      if (event.key === 'Escape') this.closeLightbox()
      if (event.key === 'ArrowLeft') this.stepLightbox(-1)
      if (event.key === 'ArrowRight') this.stepLightbox(1)
    },
    scrollReferences (direction) {
      const track = this.$refs.referenceTrack
      if (!track) return
      const amount = Math.max(460, track.clientWidth * 0.86) * direction
      track.scrollBy({ left: amount, behavior: 'smooth' })
    },
    updateReferenceScroll () {
      const track = this.$refs.referenceTrack
      if (!track) return
      const max = track.scrollWidth - track.clientWidth - 2
      this.canScrollPrev = track.scrollLeft > 2
      this.canScrollNext = track.scrollLeft < max
    },
    submitRequest () {
      this.requestMessage = 'Please leave your email below to submit this customization request.'
      const target = document.querySelector('.customize-advice')
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    },
    submitAdvice () {
      if (this.adviceSubmitting) return
      this.adviceSubmitting = true
      this.adviceMessage = ''

      submitCustomRequest({
        craft: this.selectedCraftLabel,
        plankType: this.selectedType,
        length: this.selectedLength,
        width: this.selectedWidth,
        thickness: this.selectedThickness,
        email: this.adviceForm.email,
        zip: this.adviceForm.zip,
        country: this.adviceForm.country,
        message: this.adviceForm.message
      })
        .then(() => {
          this.adviceMessage = 'Thank you. Your customization request has been submitted.'
        })
        .catch(() => {
          this.adviceMessage = 'Sorry, the request could not be submitted. Please try again later.'
        })
        .then(() => {
          this.adviceSubmitting = false
        })
    }
  }
}
</script>

<style>
.customize-page {
  color: #171411;
  background: #f6f1e9;
}

.customize-section {
  padding: clamp(88px, 10vw, 112px) 0 clamp(72px, 8vw, 88px);
}

.container--move-left {
  width: min(1360px, calc(100vw - 64px));
  margin: 0 auto;
}

.product-detail {
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(320px, 0.88fr);
  gap: clamp(48px, 5vw, 80px);
  align-items: start;
}

.customize-gallery__hero {
  margin-bottom: 20px;
}

.customize-gallery__frame {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  border: 0;
  background: #e8e2d9;
  cursor: zoom-in;
  aspect-ratio: 5 / 4;
  overflow: hidden;
  border-radius: 12px;
  box-shadow: 0 22px 44px rgba(23, 20, 17, 0.1);
}

.customize-gallery__frame img,
.customize-gallery__thumb img,
.customize-craft-card__media img,
.customize-reference-card__media img,
.customize-advice__image img,
.customize-lightbox img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.customize-gallery__corner {
  position: absolute;
  top: 22px;
  right: 22px;
  width: 35px;
  height: 35px;
}

.customize-gallery__corner::before,
.customize-gallery__corner::after {
  position: absolute;
  width: 13px;
  height: 13px;
  content: "";
}

.customize-gallery__corner::before {
  top: 3px;
  right: 3px;
  border-top: 2px solid #e2e2e2;
  border-right: 2px solid #e2e2e2;
}

.customize-gallery__corner::after {
  bottom: 3px;
  left: 3px;
  border-bottom: 2px solid #e2e2e2;
  border-left: 2px solid #e2e2e2;
}

.customize-gallery__more {
  position: absolute;
  right: 24px;
  bottom: 24px;
  min-width: 148px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 22px;
  background: rgba(246, 241, 233, 0.94);
  color: #171411;
  font-size: 14px;
  letter-spacing: 0.02em;
  pointer-events: none;
  border-radius: 999px;
}

.customize-gallery__thumbs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.customize-gallery__thumb {
  padding: 0;
  border: 2px solid transparent;
  background: #e8e2d9;
  aspect-ratio: 5 / 4;
  overflow: hidden;
  cursor: pointer;
  border-radius: 10px;
  transition: border-color 0.2s ease, opacity 0.2s ease;
}

.customize-gallery__thumb:not(.is-active):hover {
  opacity: 0.88;
}

.customize-gallery__thumb.is-active {
  border-color: #171411;
}

.product-detail__side {
  position: sticky;
  top: 96px;
  padding-left: clamp(8px, 2vw, 24px);
  border-left: 1px solid rgba(23, 20, 17, 0.1);
  padding-bottom: 20px;
}

.product-detail__title {
  margin: 0 0 28px;
  font-size: clamp(30px, 2.8vw, 44px);
  font-weight: 400;
  line-height: 1.08;
}

.customize-surface-craft {
  margin-bottom: 8px;
  padding-top: 20px;
  border-top: 1px solid rgba(23, 20, 17, 0.18);
}

.customize-surface-craft__title {
  margin: 0 0 14px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.72);
}

.customize-craft-panel {
  padding: 0 0 20px;
}

.customize-fieldset {
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.customize-fieldset--chips {
  padding-bottom: 8px;
}

.customize-fieldset legend {
  margin-bottom: 12px;
  font-size: 14px;
  letter-spacing: 0.02em;
  color: rgba(23, 20, 17, 0.58);
}

.customize-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.customize-chip {
  position: relative;
}

.customize-chip input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.customize-chip span {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  padding: 9px 14px;
  border: 1px solid rgba(23, 20, 17, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  line-height: 1.2;
  color: #171411;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.customize-chip input:checked + span {
  background: #171411;
  color: #fff;
  border-color: #171411;
}

.customize-craft-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.customize-craft-card {
  padding: 0;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.customize-craft-card__media {
  aspect-ratio: 19 / 12;
  overflow: hidden;
  background: #e8e2d9;
  border: 2px solid transparent;
  border-radius: 10px;
  transition: border-color 0.2s ease;
}

.customize-craft-card.is-active .customize-craft-card__media {
  border-color: #171411;
}

.customize-craft-card__media--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: linear-gradient(135deg, #ebe4d9 0%, #ddd4c6 100%);
}

.customize-craft-card__media--placeholder span {
  font-size: 14px;
  line-height: 1.45;
  text-align: center;
  color: rgba(23, 20, 17, 0.72);
}

.customize-craft-card span {
  display: block;
  margin-top: 10px;
  font-size: 14px;
  color: rgba(23, 20, 17, 0.72);
  padding: 0 2px;
}

.customize-craft-card.is-active span {
  color: #171411;
}

.customize-form {
  display: grid;
  gap: 22px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid rgba(23, 20, 17, 0.12);
}

.customize-request,
.customize-advice__form button {
  width: 100%;
  min-height: 56px;
  border: 0;
  border-radius: 10px;
  background: #171411;
  color: #f5f3ef;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 14px 28px rgba(23, 20, 17, 0.14);
  transition: background 0.2s ease, transform 0.2s ease;
}

.customize-request:hover,
.customize-advice__form button:hover {
  background: #2a2622;
}

.customize-feedback {
  margin: 12px 0 0;
  color: rgba(23, 20, 17, 0.68);
  font-size: 14px;
  line-height: 1.6;
}

.customize-references {
  padding: clamp(72px, 8vw, 96px) 0;
  background: #f6f1e9;
  border-top: 1px solid rgba(23, 20, 17, 0.08);
}

.customize-references__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 36px;
}

.customize-references__titles p {
  margin: 0 0 14px;
  font-size: 14px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(23, 20, 17, 0.55);
}

.customize-references__titles h2 {
  margin: 0;
  font-size: clamp(30px, 2.8vw, 44px);
  font-weight: 400;
  line-height: 1.08;
}

.customize-references__controls {
  display: flex;
  flex-shrink: 0;
  gap: 14px;
}

.customize-slider-btn {
  width: 62px;
  height: 62px;
  border: 1px solid rgba(23, 20, 17, 0.2);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.4);
  color: #171411;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease, color 0.2s ease;
}

.customize-slider-btn:hover:not(:disabled) {
  border-color: #171411;
  background: #171411;
  color: #f5f3ef;
}

.customize-slider-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.customize-references__track {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(420px, 34vw);
  gap: 34px;
  overflow-x: auto;
  padding: 0 max(24px, calc((100vw - 1360px) / 2)) 12px;
  scroll-behavior: smooth;
  scrollbar-width: thin;
}

.customize-reference-card {
  color: inherit;
  cursor: default;
}

.customize-reference-card__media {
  aspect-ratio: 16 / 10;
  overflow: hidden;
  background: #e8e2d9;
  border-radius: 10px;
}

.customize-reference-card h3 {
  margin: 14px 0 14px;
  padding: 0 14px;
  font-size: clamp(20px, 1.8vw, 24px);
  font-weight: 400;
  line-height: 1.24;
  letter-spacing: 0.02em;
}

.customize-advice {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  background: linear-gradient(180deg, #dbd5cb 0%, #d3cbc0 100%);
}

.customize-advice__image {
  min-height: 480px;
}

.customize-advice__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: clamp(56px, 7vw, 96px) clamp(40px, 6vw, 80px);
}

.customize-advice__content h2 {
  margin: 0 0 32px;
  font-size: clamp(28px, 2.6vw, 42px);
  font-weight: 400;
  line-height: 1.08;
}

.customize-advice__form {
  display: grid;
  gap: 18px;
  max-width: 480px;
  padding: 28px;
  border: 1px solid rgba(23, 20, 17, 0.08);
  border-radius: 12px;
  background: rgba(246, 241, 233, 0.48);
  box-shadow: 0 18px 38px rgba(23, 20, 17, 0.08);
}

.customize-advice__form label span {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: rgba(23, 20, 17, 0.58);
}

.customize-advice__form em {
  font-style: normal;
}

.customize-advice__form input,
.customize-advice__form select,
.customize-advice__form textarea {
  width: 100%;
  border: 1px solid rgba(23, 20, 17, 0.18);
  border-radius: 8px;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.55);
  color: #171411;
  font-size: 15px;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.customize-advice__form input:focus,
.customize-advice__form select:focus,
.customize-advice__form textarea:focus {
  outline: none;
  border-color: rgba(183, 139, 95, 0.8);
  background: #f8f4ee;
}

.customize-lightbox {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: grid;
  place-items: center;
  padding: 48px;
  background: rgba(0, 0, 0, 0.88);
}

.customize-lightbox img {
  max-width: min(1200px, 92vw);
  max-height: 82vh;
  object-fit: contain;
}

.customize-lightbox__close {
  position: absolute;
  top: 24px;
  right: 28px;
  border: 0;
  background: transparent;
  color: #fff;
  font-size: 42px;
  line-height: 1;
  cursor: pointer;
}

.customize-lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 28px;
  cursor: pointer;
}

.customize-lightbox__nav--prev {
  left: 24px;
}

.customize-lightbox__nav--next {
  right: 24px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 1100px) {
  .product-detail,
  .customize-advice {
    grid-template-columns: 1fr;
  }

  .product-detail__side {
    position: static;
    padding-left: 0;
    border-left: 0;
    padding-top: 8px;
  }

  .customize-advice__form {
    max-width: none;
  }
}

@media (max-width: 680px) {
  .customize-section {
    padding: 76px 0 56px;
  }

  .container--move-left {
    width: calc(100vw - 32px);
  }

  .product-detail {
    gap: 30px;
  }

  .product-detail__title {
    margin-bottom: 22px;
    font-size: 30px;
  }

  .customize-gallery__thumbs,
  .customize-craft-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .customize-gallery__hero {
    margin-bottom: 12px;
  }

  .customize-gallery__frame,
  .customize-gallery__thumb {
    border-radius: 8px;
  }

  .customize-surface-craft {
    padding-top: 16px;
  }

  .customize-chips {
    gap: 7px;
  }

  .customize-chip span {
    min-height: 38px;
    padding: 8px 12px;
    font-size: 13px;
  }

  .customize-form {
    gap: 18px;
    margin-top: 20px;
    padding-top: 20px;
  }

  .customize-references__head {
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 24px;
  }

  .customize-references__controls {
    align-self: flex-start;
  }

  .customize-references__track {
    grid-auto-columns: minmax(300px, 86vw);
    gap: 18px;
    padding-left: 16px;
    padding-right: 16px;
  }

  .customize-reference-card h3 {
    margin-top: 12px;
    padding: 0 10px;
    font-size: 20px;
  }

  .customize-slider-btn {
    width: 52px;
    height: 52px;
    font-size: 28px;
  }

  .customize-advice__image {
    min-height: 260px;
  }

  .customize-advice__content {
    padding: 48px 24px 56px;
  }

  .customize-advice__form {
    padding: 22px 18px;
  }

  .customize-gallery__more {
    right: 16px;
    bottom: 16px;
    min-width: auto;
    padding: 10px 14px;
    font-size: 13px;
  }

  .customize-lightbox {
    padding: 70px 16px 24px;
  }

  .customize-lightbox__close {
    top: 18px;
    right: 16px;
  }

  .customize-lightbox__nav {
    top: auto;
    bottom: 22px;
    transform: none;
  }

  .customize-lightbox__nav--prev {
    left: 16px;
  }

  .customize-lightbox__nav--next {
    right: 16px;
  }
}

@media (max-width: 480px) {
  .customize-gallery__thumbs,
  .customize-craft-grid {
    gap: 10px;
  }

  .customize-craft-card span {
    font-size: 13px;
    line-height: 1.35;
  }

  .customize-advice__content {
    padding: 42px 16px 50px;
  }

  .customize-advice__form {
    padding: 20px 14px;
  }
}
</style>
